import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import { getCampaignEventsAnalytics } from 'services/MailingService';
import { errorToast } from 'utils/toast';
import { getLiteral } from 'utils/getLiteral';

const PRESETS = {
    CUSTOM: 'custom',
    TODAY: 'today',
    YESTERDAY: 'yesterday',
    SEVEN_DAYS: '7D',
    THIRTY_DAYS: '30D',
    THREE_MONTHS: '3M',
    SIX_MONTHS: '6M',
    TWELVE_MONTHS: '12M',
};

const DEFAULT_PRESET = PRESETS.THIRTY_DAYS;

const INITIAL_CUSTOM_RANGE = {
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
};

/** @param {'start'|'end'} bound */
export function resolveCustomRangeInstant(date, time, bound) {
    if (!date) {
        return null;
    }
    if (time instanceof Date && !Number.isNaN(time.getTime())) {
        const combined = new Date(date);
        combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
        return combined;
    }
    const fallback = new Date(date);
    if (bound === 'end') {
        fallback.setHours(23, 59, 59, 999);
    } else {
        fallback.setHours(0, 0, 0, 0);
    }
    return fallback;
}

/** @param {Date} date */
export function startOfLocalDay(date) {
    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

/**
 * @param {Date|null|undefined} a
 * @param {Date|null|undefined} b
 * @returns {Date|null}
 */
export function maxLocalCalendarDayDate(a, b) {
    if (!a && !b) {
        return null;
    }
    const da = a ? startOfLocalDay(a) : null;
    const db = b ? startOfLocalDay(b) : null;
    if (!da) {
        return db;
    }
    if (!db) {
        return da;
    }
    return da.getTime() >= db.getTime() ? da : db;
}

/**
 * Provisional: ISO 8601 in UTC (`toISOString()`). Change here if the analytics API expects another shape.
 * @param {Date} date
 * @returns {string}
 */
function formatAnalyticsRangeInstant(date) {
    return date.toISOString();
}

/**
 * @param {{ preset: string, customPresetValue: string, customRange: typeof INITIAL_CUSTOM_RANGE }}
 * @returns {{ shouldFetch: boolean, preset?: string, from?: string, to?: string }}
 */
function buildCampaignAnalyticsParams({ preset, customPresetValue, customRange }) {
    if (preset !== customPresetValue) {
        return { shouldFetch: true, preset, from: undefined, to: undefined };
    }

    const fromDate = resolveCustomRangeInstant(
        customRange.startDate,
        customRange.startTime,
        'start',
    );
    const toDate = resolveCustomRangeInstant(customRange.endDate, customRange.endTime, 'end');

    if (!fromDate || !toDate || fromDate.getTime() > toDate.getTime()) {
        return { shouldFetch: false, preset: undefined, from: undefined, to: undefined };
    }

    return {
        shouldFetch: true,
        preset: undefined,
        from: formatAnalyticsRangeInstant(fromDate),
        to: formatAnalyticsRangeInstant(toDate),
    };
}

const normalizeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0;
};

const normalizePoints = (points = []) => {
    if (!Array.isArray(points)) {
        return [];
    }
    return points.map((point) => ({
        timestamp: point?.timestamp || null,
        uniqueOpens: normalizeNumber(point?.uniqueOpens),
        uniqueClicks: normalizeNumber(point?.uniqueClicks),
    }));
};

function cloneDate(value) {
    return value instanceof Date && !Number.isNaN(value.getTime())
        ? new Date(value.getTime())
        : null;
}

/** @param {typeof INITIAL_CUSTOM_RANGE} range */
export function cloneCustomRangeState(range) {
    return {
        startDate: cloneDate(range?.startDate),
        endDate: cloneDate(range?.endDate),
        startTime: cloneDate(range?.startTime),
        endTime: cloneDate(range?.endTime),
    };
}

const TIME_ANCHOR = { y: 2000, m: 0, d: 1 };

/**
 * Ensures custom range is not entirely before the campaign send instant.
 * @param {typeof INITIAL_CUSTOM_RANGE} range
 * @param {Date|null|undefined} sendAt
 */
export function clampCustomRangeToSendAt(range, sendAt) {
    if (!sendAt || Number.isNaN(sendAt.getTime())) {
        return cloneCustomRangeState(range);
    }
    const next = cloneCustomRangeState(range);
    const sendMs = sendAt.getTime();

    let from = resolveCustomRangeInstant(next.startDate, next.startTime, 'start');
    if (!from || from.getTime() < sendMs) {
        const day = startOfLocalDay(sendAt);
        if (day) {
            next.startDate = day;
        }
        next.startTime = new Date(
            TIME_ANCHOR.y,
            TIME_ANCHOR.m,
            TIME_ANCHOR.d,
            sendAt.getHours(),
            sendAt.getMinutes(),
            0,
            0,
        );
        from = resolveCustomRangeInstant(next.startDate, next.startTime, 'start');
    }

    const to = resolveCustomRangeInstant(next.endDate, next.endTime, 'end');
    if (from && to && to.getTime() < from.getTime()) {
        next.endDate = new Date(from);
        next.endTime = new Date(
            TIME_ANCHOR.y,
            TIME_ANCHOR.m,
            TIME_ANCHOR.d,
            from.getHours(),
            from.getMinutes(),
            0,
            0,
        );
    }
    return next;
}

export const usePerformanceOverTime = ({ campaignId, isActive }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [preset, setPreset] = useState(DEFAULT_PRESET);
    const [customRange, setCustomRange] = useState(INITIAL_CUSTOM_RANGE);
    const requestGenerationRef = useRef(0);

    const handlePresetChange = useCallback((newPreset) => {
        setPreset(newPreset);
        if (newPreset !== PRESETS.CUSTOM) {
            setCustomRange(INITIAL_CUSTOM_RANGE);
        }
    }, []);

    const analyticsRequest = useMemo(
        () =>
            buildCampaignAnalyticsParams({
                preset,
                customPresetValue: PRESETS.CUSTOM,
                customRange,
            }),
        [customRange, preset],
    );

    const analyticsShouldFetch = analyticsRequest.shouldFetch;
    const analyticsPreset = analyticsRequest.preset;
    const analyticsFrom = analyticsRequest.from;
    const analyticsTo = analyticsRequest.to;

    useEffect(() => {
        if (!isActive || campaignId == null || campaignId === '') {
            setData(null);
            setIsLoading(false);
            return undefined;
        }

        if (!analyticsShouldFetch) {
            setData(null);
            setIsLoading(false);
            return undefined;
        }

        const generation = ++requestGenerationRef.current;
        setIsLoading(true);
        setData(null);

        getCampaignEventsAnalytics(campaignId, {
            preset: analyticsPreset,
            from: analyticsFrom,
            to: analyticsTo,
        })
            .then((response) => {
                if (generation !== requestGenerationRef.current) {
                    return;
                }
                const normalized = {
                    granularity: response?.performanceOverTime?.granularity || 'day',
                    points: normalizePoints(response?.performanceOverTime?.points),
                };
                setData(normalized);
            })
            .catch(() => {
                if (generation !== requestGenerationRef.current) {
                    return;
                }
                errorToast({ text: getLiteral('error_generalerror') });
                setData(null);
            })
            .finally(() => {
                if (generation === requestGenerationRef.current) {
                    setIsLoading(false);
                }
            });

        return () => {
            requestGenerationRef.current += 1;
        };
    }, [analyticsShouldFetch, analyticsFrom, analyticsPreset, analyticsTo, campaignId, isActive]);

    const applyCustomRange = useCallback((next) => {
        const cloned = cloneCustomRangeState(next);
        const draftRequest = buildCampaignAnalyticsParams({
            preset: PRESETS.CUSTOM,
            customPresetValue: PRESETS.CUSTOM,
            customRange: cloned,
        });
        if (!draftRequest.shouldFetch) {
            return;
        }
        setPreset(PRESETS.CUSTOM);
        setCustomRange(cloned);
    }, []);

    return {
        applyCustomRange,
        customRange,
        data,
        isLoading,
        preset,
        setPreset: handlePresetChange,
        presets: PRESETS,
    };
};
